import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import CheckIcons from '@/icons/CheckIcons'

interface OnboardingSuccessProps {
    employeeName?: string
    onFinish?: () => void
}

const OnboardingSuccess: React.FC<OnboardingSuccessProps> = ({
    employeeName = 'Bạn',
    onFinish,
}) => {
    const { t } = useTranslation()
    const [showConfetti, setShowConfetti] = useState(true)
    const [showContent, setShowContent] = useState(false)

    useEffect(() => {
        // Delay content animation
        const timer = setTimeout(() => {
            setShowContent(true)
        }, 300)

        // Stop confetti after 5 seconds
        const confettiTimer = setTimeout(() => {
            setShowConfetti(false)
        }, 5000)

        return () => {
            clearTimeout(timer)
            clearTimeout(confettiTimer)
        }
    }, [])

    // Confetti animation
    const confettiPieces = Array.from({ length: 50 }, (_, i) => (
        <div
            key={i}
            className={`absolute w-2.5 h-2.5 top-0 z-50 transition-opacity duration-1000 ${showConfetti ? 'animate-confetti-fall' : 'opacity-0'}`}
            style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                backgroundColor: [
                    '#FFD700',
                    '#FF6B6B',
                    '#4ECDC4',
                    '#45B7D1',
                    '#96CEB4',
                    '#FFEAA7',
                ][Math.floor(Math.random() * 6)],
                animationDuration: `${3 + Math.random() * 2}s`,
            }}
        />
    ))

    return (
        <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 flex items-center justify-center z-50">
            {/* Confetti */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {confettiPieces}
            </div>

            <div
                className={`max-w-2xl mx-auto text-center p-8 transition-all duration-1000 ${showContent ? 'animate-slide-up-custom' : 'opacity-0'}`}
            >
                {/* Success Icon */}
                <div className="flex justify-center mb-8">
                    <div
                        className={`bg-green-100 rounded-full p-8 ${showContent ? 'animate-bounce-custom' : ''}`}
                        style={{
                            boxShadow: showContent
                                ? '0 0 20px rgba(34, 197, 94, 0.3)'
                                : '',
                            animation: showContent
                                ? 'bounce-custom 1s, pulse-glow 2s ease-in-out infinite'
                                : '',
                        }}
                    >
                        <CheckIcons className="w-24 h-24 text-green-600" />
                    </div>
                </div>

                {/* Main Message */}
                <div className="space-y-6">
                    <div>
                        <Typogaphy
                            size="large"
                            className="font-bold text-gray-800 mb-4 text-2xl"
                        >
                            🎉 {t('congratulations')} {employeeName}! 🎉
                        </Typogaphy>
                        <Typogaphy
                            size="large"
                            className="text-green-600 font-semibold mb-2 text-xl"
                        >
                            {t('onboarding-complete')}
                        </Typogaphy>
                    </div>

                    <div className="space-y-4">
                        <Typogaphy
                            size="medium"
                            className="text-gray-600 leading-relaxed"
                        >
                            {t('welcome-team-message')}
                        </Typogaphy>
                        <Typogaphy
                            size="medium"
                            className="text-gray-600 leading-relaxed"
                        >
                            {t('profile-submitted-success')}
                        </Typogaphy>
                    </div>

                    {/* Achievement Cards */}
                    <div className="grid md:grid-cols-3 gap-4 mt-8">
                        <div className="bg-white rounded-lg p-4 shadow-md border border-green-200">
                            <div className="text-green-600 text-2xl mb-2">
                                ✅
                            </div>
                            <Typogaphy
                                size="small"
                                className="font-semibold text-gray-700"
                            >
                                {t('profile-completed')}
                            </Typogaphy>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-md border border-blue-200">
                            <div className="text-blue-600 text-2xl mb-2">
                                📋
                            </div>
                            <Typogaphy
                                size="small"
                                className="font-semibold text-gray-700"
                            >
                                {t('documents-uploaded')}
                            </Typogaphy>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-md border border-purple-200">
                            <div className="text-purple-600 text-2xl mb-2">
                                🚀
                            </div>
                            <Typogaphy
                                size="small"
                                className="font-semibold text-gray-700"
                            >
                                {t('ready-to-start')}
                            </Typogaphy>
                        </div>
                    </div>

                    {/* Next Steps */}
                    <div className="bg-blue-50 rounded-lg p-6 mt-8">
                        <Typogaphy
                            size="medium"
                            className="font-semibold text-blue-800 mb-3"
                        >
                            {t('what-happens-next')}
                        </Typogaphy>
                        <div className="space-y-2 text-left">
                            <div className="flex items-start space-x-3">
                                <div className="text-blue-600 mt-1">1️⃣</div>
                                <Typogaphy
                                    size="small"
                                    className="text-blue-700"
                                >
                                    {t('hr-will-review-info')}
                                </Typogaphy>
                            </div>
                            <div className="flex items-start space-x-3">
                                <div className="text-blue-600 mt-1">2️⃣</div>
                                <Typogaphy
                                    size="small"
                                    className="text-blue-700"
                                >
                                    {t('account-activation-soon')}
                                </Typogaphy>
                            </div>
                            <div className="flex items-start space-x-3">
                                <div className="text-blue-600 mt-1">3️⃣</div>
                                <Typogaphy
                                    size="small"
                                    className="text-blue-700"
                                >
                                    {t('welcome-email-coming')}
                                </Typogaphy>
                            </div>
                        </div>
                    </div>

                    {/* Action Button */}
                    <div className="mt-8 flex justify-center w-full">
                        <Button
                            variant="contained"
                            size="large"
                            onClick={onFinish}
                            className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white px-8 py-3 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                        >
                            {t('finish-onboarding')} ✨
                        </Button>
                    </div>

                    {/* Footer Message */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <Typogaphy size="small" className="text-gray-500">
                            {t('thank-you-for-joining')} 💙
                        </Typogaphy>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OnboardingSuccess
